// Decouples the API layer from the connection-required prompt UI: api code can
// publish a "connect your account first" signal (a 412 connection_required
// response) without importing any component (no import cycle). The
// ConnectionRequiredSheet registers the handler on mount — mirror of
// authBridge.ts's lock-handler pattern.

export type ConnectionProvider = 'zoho' | 'azure_devops' | 'discourse' | 'microsoft';

/** Human-readable provider names for prompts/toasts. */
export const CONNECTION_PROVIDER_LABELS: Record<ConnectionProvider, string> = {
  zoho: 'Zoho Desk',
  azure_devops: 'Azure DevOps',
  discourse: 'Discourse',
  microsoft: 'Microsoft 365',
};

/** What the user chose at the "connect your account" prompt. */
export type ConnectionPromptDecision = 'connect' | 'use-global' | 'cancel';

export interface ConnectionPromptRequest {
  provider: ConnectionProvider;
  /** True when the backend says this action MAY fall back to the shared/global
   *  connection (admin-enabled) — the sheet then offers "Use shared connection". */
  globalFallbackAvailable: boolean;
}

let promptHandler:
  | ((req: ConnectionPromptRequest, resolve: (d: ConnectionPromptDecision) => void) => void)
  | null = null;

export function registerConnectionPromptHandler(
  fn: (req: ConnectionPromptRequest, resolve: (d: ConnectionPromptDecision) => void) => void,
): void {
  promptHandler = fn;
}

// Components that handle connection_required LOCALLY (e.g. the AI composer's
// inline "draft is kept" banner) suppress the global sheet while mounted so
// the user never sees two prompts for one failure. Counter, not boolean —
// nested/overlapping composers compose correctly.
let suppressCount = 0;

/** Suppress the global prompt while mounted. Returns the release fn (call on unmount). */
export function suppressConnectionPrompt(): () => void {
  suppressCount++;
  let released = false;
  return () => {
    if (!released) { released = true; suppressCount--; }
  };
}

/**
 * Promise-based "connect your account first" prompt. Resolves with the user's
 * decision: 'connect' (go to Profile), 'use-global' (use the shared connection
 * for this one action — only offered when globalFallbackAvailable), or 'cancel'.
 * Resolves 'cancel' IMMEDIATELY when a component owns the 412 surface (suppress)
 * or no sheet is mounted — the caller then just rethrows so the local owner
 * (e.g. the AI composer's inline banner) handles it.
 */
export function promptConnectionRequired(
  provider: ConnectionProvider,
  globalFallbackAvailable = false,
): Promise<ConnectionPromptDecision> {
  if (suppressCount > 0 || !promptHandler) return Promise.resolve('cancel');
  return new Promise((resolve) => promptHandler!({ provider, globalFallbackAvailable }, resolve));
}

/** Back-compat fire-and-forget: raise the connect prompt without awaiting it
 *  (the no-fallback path — just nudges the user to connect their account). */
export function notifyConnectionRequired(provider: ConnectionProvider): void {
  void promptConnectionRequired(provider, false);
}
